import { useState, useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Megaphone } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { useAuth } from '@/hooks/useAuth';
import { MarketingActionsModal } from '@/components/marketing/MarketingActionsModal';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import {
  Badge,
  Button,
  DataTable,
} from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import type { FilterValue } from '@/types/filters.types';
import { getStageBadgeVariant } from '@/lib/stageBadgeVariant';
import { useDebounce } from '@/hooks/useDebounce';
import { usePageContext } from '@/hooks/usePageContext';
import { buildApiParams, buildMarketingFilterConditions, extractSearch } from '@/lib/filterRegistry';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import * as plansSvc from '@/services/plans.service';
import type { PlanListItem, PlanListParams } from '@/services/plans.service';
import { appointmentsService } from '@/services/appointments.service';
import type { AppointmentType as ApptType } from '@/services/appointments.service';
import { usePlanFilterOptions } from '@/hooks/filters';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { useRoleTitles } from '@/hooks/useRoleTitles';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function RelativeOrCompactDate({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const date = new Date(value);
  if (isNaN(date.getTime())) return <span className="text-xs">{value}</span>;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return <span className="text-xs text-slate-600">Today</span>;
  if (diffDays === 1) return <span className="text-xs text-slate-600">Yesterday</span>;
  if (diffDays < 7) return <span className="text-xs text-slate-600">{diffDays} days ago</span>;
  if (diffDays < 30) return <span className="text-xs text-slate-600">{Math.floor(diffDays / 7)}w ago</span>;

  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const year = date.getFullYear();
  return (
    <span className="flex flex-col leading-tight text-xs">
      <span className="text-slate-700">{monthDay}</span>
      <span className="text-[10px] text-slate-400">{year}</span>
    </span>
  );
}

function CompactDate({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const d = new Date(value);
  if (isNaN(d.getTime())) return <span className="text-xs">{value}</span>;
  const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const year = d.getFullYear();
  return (
    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
      <span className="text-xs">{monthDay}</span>
      <span className="text-xs text-slate-400">{year}</span>
    </span>
  );
}

// ─── Filter configs (shared -- single source of truth) ───────────────────────

import { buildPlanFilterConfigs } from './plansShared';

/** Map URL ?view= param to stage name substring for auto-selection */
const VIEW_STAGE_MAP: Record<string, string> = {
  inquiries: 'inquiry',
  surgery: 'surg',
  postop: 'post',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

/**
 * `?view=consults` used to lock this list to the first stage named like
 * "consult". Consults is its own list now (/consults), so an old link or
 * bookmark lands there instead.
 */
export default function PlansPage() {
  const [searchParams] = useSearchParams();

  if (searchParams.get('view')?.toLowerCase() === 'consults') {
    return <Navigate to="/consults" replace />;
  }

  return <PlansList />;
}

function PlansList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();

  // ─── Role titles ────────────────────────────────────────────────────────
  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'MD';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildPlanFilterConfigs(doctorLabel, coordLabel),
    [doctorLabel, coordLabel],
  );

  // ─── View scope (?view=inquiries|surgery|postop) ───────────────────────
  // A ?view= list is hard-locked to its stage: the Stage filter is hidden and
  // the stage is applied as an override below. It is deliberately never written
  // into persisted filter state — an earlier build did that, and the value
  // survived navigation and left the unscoped Plans list stuck on that stage.
  const viewParam = searchParams.get('view');
  const viewKey = viewParam?.toLowerCase() ?? '';
  const isStageView = Boolean(viewKey && VIEW_STAGE_MAP[viewKey]);

  // ─── Filter state (localStorage-persisted) ────────────────────────────
  // Key bumped to -v2 to abandon states polluted by the stray stage above.
  const filterState = useLocalDashboardFilters(filterConfigs, 'plans-v2');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const search = extractSearch(effectiveValues);
  const debouncedSearch = useDebounce(search, 300);

  // Search is sent separately as a debounced top-level param, so keep it out of
  // the values fed to buildApiParams. Otherwise each commit changes the query
  // key twice: once with the term stripped (debounce still stale), then again
  // 300ms later with it — a visible flash of unfiltered results.
  const searchlessValues = useMemo(() => {
    const { search: _search, ...rest } = effectiveValues;
    void _search;
    return rest;
  }, [effectiveValues]);

  const [page, setPage] = useState(1);
  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'plans', entityLabel: 'plans' });
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);

  const updateAIContext = usePageContext('Plans');

  // The stat cards count across every stage, so they ignore both the user's
  // stage filter and the view lock.
  const stageStatsValues = useMemo(() => {
    const { 'plan-stages': _stage, ...rest } = searchlessValues;
    void _stage;
    return rest;
  }, [searchlessValues]);

  // ─── Queries ───────────────────────────────────────────────────────────

  const stageStatsParams = useMemo(() => {
    const params = buildApiParams(stageStatsValues, filterState.negations);
    return debouncedSearch ? { ...params, search: debouncedSearch } : params;
  }, [stageStatsValues, filterState.negations, debouncedSearch]);

  const { data: stageStatsData } = useQuery({
    queryKey: ['stage-stats', stageStatsParams],
    queryFn: () => plansSvc.getStageStats(stageStatsParams),
  });

  // ─── Stage lock for ?view= lists ───────────────────────────────────────
  // Resolved from the stage list rather than stored, so it applies only while
  // the view is open and can never persist into the plain Plans list.
  const lockedStageId = useMemo(() => {
    const stageSubstr = isStageView ? VIEW_STAGE_MAP[viewKey] : undefined;
    if (!stageSubstr || !stageStatsData?.length) return undefined;
    const match = stageStatsData.find(
      (s) => s.stageName.toLowerCase().includes(stageSubstr),
    );
    return match ? String(match.stageId) : undefined;
  }, [isStageView, viewKey, stageStatsData]);

  // 'plan-stages' is the canonical config key the API maps to stage IDs.
  const filterOnlyValues = useMemo<Record<string, FilterValue>>(
    () => (lockedStageId
      ? { ...searchlessValues, 'plan-stages': [lockedStageId] }
      : searchlessValues),
    [searchlessValues, lockedStageId],
  );

  const filterParams = useMemo(
    () => buildApiParams(filterOnlyValues, filterState.negations),
    [filterOnlyValues, filterState.negations],
  );

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterParams]);

  // ─── Marketing modal filter conditions (same format as dynamic lists) ─
  // Built from the same values as the list (lock included) so the audience
  // always matches what is on screen.
  const marketingFilterConditions = useMemo(
    () => buildMarketingFilterConditions('plan', filterOnlyValues, filterState.negations),
    [filterOnlyValues, filterState.negations],
  );

  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60 * 1000,
  });

  const apiParams = useMemo<PlanListParams>(() => ({
    ...filterParams,
    search: debouncedSearch || undefined,
    page,
    perPage: PAGE_SIZE,
  }), [filterParams, debouncedSearch, page]);

  // On a locked view the stage is only known once the stage list has loaded, so
  // hold the first fetch until then — otherwise the full plan list flashes up
  // before narrowing. Gated on the stats having loaded rather than on a stage
  // actually matching, so an account with no such stage still shows results.
  const stageLockPending = isStageView && stageStatsData === undefined;

  const { data: plansData, isLoading: plansQueryLoading, isPlaceholderData } = useQuery({
    queryKey: ['plans', apiParams],
    queryFn: () => plansSvc.listPlans(apiParams),
    placeholderData: (prev) => prev,
    enabled: !stageLockPending,
  });

  // Skeleton rows whenever the visible rows are stale: initial load, a pending
  // stage lock, previous results still on screen while a new request is in
  // flight, or a committed search still waiting out the debounce.
  // isPlaceholderData covers the third case — placeholderData keeps isLoading
  // false, so without it the list sat unchanged with no sign a search was
  // running. searchPending covers the 300ms before the request even starts.
  const searchPending = search !== debouncedSearch;
  const plansLoading =
    plansQueryLoading || stageLockPending || isPlaceholderData || searchPending;

  // ─── Derived data ──────────────────────────────────────────────────────

  const plans = plansData?.data ?? [];
  const total = plansData?.meta?.total ?? 0;
  const totalRevenue = useMemo(
    () => plans.reduce((sum, p) => sum + (p.revenueOpportunity ?? 0), 0),
    [plans],
  );

  // ─── Sidebar options (shared hook — single source of truth) ──────────

  const sidebarOptions = usePlanFilterOptions();

  // ── Appointment-type merge-by-name plumbing ──────────────────────────
  const apptTypeOptions = useMemo(
    () => (appointmentTypes ?? [])
      .filter((at: ApptType) => at.appointmentTypeGroupActive !== false)
      .map((at: ApptType) => ({
        value: String(at.appointmentTypeId),
        label: at.appointmentTypeName,
      })),
    [appointmentTypes],
  );

  const apptTypeNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const t of apptTypeOptions) {
      const key = t.label.toLowerCase();
      const ids = map.get(key) ?? [];
      ids.push(t.value);
      map.set(key, ids);
    }
    return map;
  }, [apptTypeOptions]);

  const apptTypeIdToRep = useMemo(() => {
    const map = new Map<string, string>();
    for (const [, ids] of apptTypeNameToIds) {
      const rep = ids[0];
      for (const id of ids) map.set(id, rep);
    }
    return map;
  }, [apptTypeNameToIds]);

  const sidebarValues = useMemo<Record<string, FilterValue>>(() => {
    const raw = effectiveValues['appointmentType'];
    if (!Array.isArray(raw) || raw.length === 0) return effectiveValues;
    const mapped = new Set<string>();
    for (const id of raw) {
      const rep = apptTypeIdToRep.get(id);
      if (rep) mapped.add(rep);
    }
    return { ...effectiveValues, appointmentType: [...mapped] };
  }, [effectiveValues, apptTypeIdToRep]);

  const handleFilterChange = useCallback(
    (key: string, value: FilterValue) => {
      if (key !== 'appointmentType' || !Array.isArray(value)) {
        filterState.setFilter(key, value);
        return;
      }
      const expanded: string[] = [];
      for (const rep of value) {
        const name = apptTypeOptions.find((t) => t.value === rep)?.label;
        if (name) {
          const ids = apptTypeNameToIds.get(name.toLowerCase()) ?? [rep];
          expanded.push(...ids);
        } else {
          expanded.push(rep);
        }
      }
      filterState.setFilter('appointmentType', expanded);
    },
    [filterState, apptTypeOptions, apptTypeNameToIds],
  );

  // ─── AI sidebar context ────────────────────────────────────────────────

  const stageStatBySubstr = useCallback(
    (substr: string) => (stageStatsData ?? []).find((s) => s.stageName.toLowerCase().includes(substr))?.count ?? 0,
    [stageStatsData],
  );

  const activeFilters = useMemo(
    () =>
      Object.entries(effectiveValues)
        .filter(([key, val]) => {
          if (key === 'search') return false;
          if (Array.isArray(val)) return val.length > 0;
          if (typeof val === 'object' && val !== null && 'from' in val) return !!(val.from || val.to);
          return false;
        })
        .map(([key]) => key)
        .join(', '),
    [effectiveValues],
  );

  useEffect(() => {
    const parts: string[] = [];
    if (search) parts.push(`Search: "${search}"`);
    if (activeFilters) parts.push(activeFilters);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    updateAIContext({
      filters: parts.length > 0 ? parts.join(', ') : 'None',
      dataSummary: `${total.toLocaleString()} plan${total !== 1 ? 's' : ''} — page ${page} of ${totalPages}`,
    });
  }, [search, activeFilters, page, total, updateAIContext]);

  // ─── Table columns ─────────────────────────────────────────────────────

  const columns: DataTableColumn<PlanListItem>[] = [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      width: 'w-[70px]',
      render: (row) => <CompactDate value={row.date} />,
    },
    {
      key: 'patientName',
      header: 'Patient',
      sortable: true,
      render: (row) => (
        <span className="font-medium text-slate-900">{row.patientName}</span>
      ),
    },
    {
      key: 'planTypeName',
      header: 'Plan Type',
      render: (row) => <span className="text-slate-700 text-sm">{row.planTypeName ?? '—'}</span>,
    },
    {
      key: 'revenueOpportunity',
      header: '$ Opportunity',
      render: (row) => (
        <span className="text-slate-700 tabular-nums">
          {row.revenueOpportunity != null ? `$${row.revenueOpportunity.toLocaleString()}` : '—'}
        </span>
      ),
    },
    {
      key: 'doctorName',
      header: doctorLabel,
      render: (row) => <span className="text-slate-700">{row.doctorName ?? '—'}</span>,
    },
    {
      key: 'coordName',
      header: coordLabel,
      render: (row) => <span className="text-slate-700">{row.coordName ?? '—'}</span>,
    },
    {
      key: 'stageName',
      header: 'Stage',
      render: (row) =>
        row.stageName ? (
          <div className="flex flex-col items-start gap-1">
            <Badge variant={getStageBadgeVariant(row.stageName)} dot size="sm">
              {row.stageName}
            </Badge>
            {row.stageSubName && (
              <Badge variant="neutral" size="sm">
                {row.stageSubName}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        ),
    },
    {
      key: 'facilityName',
      header: 'Facility',
      render: (row) => <span className="text-slate-700 text-xs">{row.facilityName ?? '—'}</span>,
    },
    {
      key: 'lastContact',
      header: 'Last Contact',
      sortable: true,
      width: 'w-[110px]',
      render: (row) => <RelativeOrCompactDate value={row.lastContact} />,
    },
  ];

  // ─── Derived view title ─────────────────────────────────────────────────

  const VIEW_TITLES: Record<string, string> = {
    inquiries: 'Inquiries',
    surgery: 'Surgery',
    postop: 'Post Op',
  };
  const pageTitle = VIEW_TITLES[viewKey] || 'Plans';
  const isInquiriesView = viewKey === 'inquiries';

  // A ?view= list is locked to its stage, so the Stage filter is hidden rather
  // than shown with a selection the user cannot meaningfully change.
  const visibleFilterConfigs = useMemo(
    () => isStageView ? filterConfigs.filter((f) => f.key !== 'plan-stages') : filterConfigs,
    [filterConfigs, isStageView],
  );

  function handleExport() {
    startExport({ ...filterParams, search: debouncedSearch || undefined });
  }

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={visibleFilterConfigs}
            values={sidebarValues}
            options={sidebarOptions}
            negations={filterState.negations}
            onChange={handleFilterChange}
            onNegateChange={filterState.setNegation}
            onClear={filterState.clearFilter}
            searchCommitMode="blur-or-enter"
            showAIAssistant={false}
            storageKey="plans"
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-6 p-6">
            <PageHeader
              variant="compact"
              title={pageTitle}
              actions={(
                <>
                  {hasPermission(20) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Megaphone className="h-4 w-4" />}
                      onClick={() => setMarketingModalOpen(true)}
                    >
                      Email / SMS List
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    disabled={total === 0 || exporting}
                    loading={exporting}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  {exportModal}
                </>
              )}
            />

            {!isInquiriesView && (
              <MetricCardGrid columns={4}>
                <StatCard label="Total Plans" value={total} />
                <StatCard label="Inquiries" value={stageStatBySubstr('inquiry')} />
                <StatCard label="Consults" value={stageStatBySubstr('consult')} />
                <StatCard label="Revenue Opportunity" value={`$${totalRevenue.toLocaleString()}`} />
              </MetricCardGrid>
            )}

          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <DataTable<PlanListItem>
              data={plans}
              columns={columns}
              keyField="planId"
              loading={plansLoading}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              tableFixed
              className="[&>div]:overflow-x-hidden"
              onRowClick={(row) => navigate(`/patients/${row.patientId}/plans/${row.planId}`)}
              emptyTitle="No plans found"
              emptyDescription="Try adjusting your search or filters."
            />
          </div>
        </div>
      </ListPageLayout>

      {hasPermission(20) && (
        <MarketingActionsModal
          isOpen={marketingModalOpen}
          onClose={() => setMarketingModalOpen(false)}
          sourceType="plan"
          filterConditions={marketingFilterConditions}
        />
      )}
    </>
  );
}
